import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';

import { Helmet } from 'react-helmet';
import { createRoom } from '../../../../graphql/mutations';
import { AuthToken } from '../../../../services';

import {
  OfferContext, PaymentContext, ProfileContext, ViewContext, ViewModalActionTypes,
} from '../../../../stores/contexts';
import { useCurrency, useTrade, useOffer } from '../../../../stores/hooks';

import { AppElement, AppFC, OfferInterface } from '../../../../interfaces';
import { OfferTradeDataProps } from './types';
import {
  ButtonCategory, GRAPHQL_CHAT_API, OfferStatusTypes, OfferTypes, Paths,
} from '../../../../constants';
import { getTradeLimitMessage } from '../../../../utils';

import { OfferTradeDetails } from '../../../Organisms';
import { FormInput, UserInfo } from '../../../Molecules';
import {
  Button, Card, Loader,
} from '../../../Atoms';
import { ExclamationIcon } from '../../../Atoms/icons';
import { TradeCreateDataProps } from '../../../../services/httpRequest';

const { REGION } = GRAPHQL_CHAT_API;

Amplify.configure({
  API: {
    GraphQL: {
      endpoint: process.env.REACT_APP_GRAPHQL_CHAT_API_URL as string,
      region: REGION,
      defaultAuthMode: 'lambda',
    },
  },
});

export const CreateTrade: AppFC<{ tradeType: OfferTypes }> = ({ tradeType }): AppElement => {
  const [isLoading, setIsLoading] = useState(false);
  const [offer, setOffer] = useState<OfferInterface>();
  const { paymentState: { methods: paymentMethods } } = useContext(PaymentContext);
  const currentMethod = paymentMethods.find(({ name }) => name === offer?.paymentMethodTypes[0]);

  const client = generateClient();

  const tokens = new AuthToken();
  const accessToken = tokens.getAccessToken();

  const { profileState: { id } } = useContext(ProfileContext);
  const { offerState: { currencyRate: defaultRate } } = useContext(OfferContext);
  const { updateViewModal } = useContext(ViewContext);

  const isSeller = tradeType === OfferTypes.Buy;
  const isBuyer = tradeType === OfferTypes.Sell;

  const { title: limitsTitle, value: limitsValue } = getTradeLimitMessage(offer as OfferInterface);
  const { offerId } = useParams();
  const navigate = useNavigate();

  const { getFiatRate } = useCurrency();
  const {
    createTrade,
    getTrades,
  } = useTrade();
  const { getOfferById } = useOffer();
  const [currencyRate, setCurrencyRate] = useState(defaultRate);

  const validationSchema = yup.object().shape({
    demandedAmount: yup.number()
      .typeError('Please enter a valid number')
      .required('demanded amount is required')
      .min(offer?.minTrade || 0)
      .max(offer?.maxTrade || Infinity),
    offeredAmount: yup.number().required(),
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: {
      errors,
    },
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const price = ((Number(currencyRate?.rate || 0) * (100 + (offer?.margin || 0))) / 100);

  const demandedAmount = (watch('demandedAmount') || 0);

  useEffect(() => {
    if (offerId) {
      Promise.all([getOfferById(offerId, { OfferStatus: OfferStatusTypes.Open }), getTrades(),
      ]).then((res) => {
        setOffer(res[0]);
      }).catch(() => {
        setOffer({} as OfferInterface);
      });
    }
  }, [offerId]);

  useEffect(() => {
    if (offer?.id) {
      getFiatRate(offer?.demandedCurrency, offer?.offeredCurrency)
        .then((res) => {
          console.log('Currency rate:', res);
          setCurrencyRate(res);
        });
    }
  }, [offer]);

  useEffect(() => {
    console.log(demandedAmount);
    if (typeof Number(demandedAmount) === 'number') {
      setValue('offeredAmount', demandedAmount * price);
    }
  }, [demandedAmount]);

  const onSubmit: SubmitHandler<OfferTradeDataProps> = (data) => {
    if (offer) {
      // const currentDemandedAmount = demandedAmount - (demandedAmount * offer.margin) / 100;

      const requestBody: TradeCreateDataProps = {
        offerId: offer.id,
        paymentMethodType: offer.paymentMethodTypes[0],
        offeredAmount: Number(data.offeredAmount),
        demandedAmount,
      };
      if (data.description) {
        requestBody.description = data.description;
      }

      setIsLoading(true);
      createTrade(requestBody)
        .then((tradeId) => {
          client.graphql({
            authToken: accessToken,
            query: createRoom,
            variables: {
              input: {
                name: tradeId,
                userId: id as string,
              },
            },
          }).then(() => {
            setIsLoading(false);
            if (tradeId) {
              navigate(`/trades/${tradeId}`);
            }
          }).catch(() => {
            updateViewModal({
              type: ViewModalActionTypes.FailModal,
              payload: {
                title: 'Trade chat room create failed!',
              },
            });
            setIsLoading(false);
          });
        }).catch((reason) => {
          updateViewModal({
            type: ViewModalActionTypes.FailModal,
            payload: {
              title: 'Trade create failed!',
              description: reason.response?.data?.detail || '',
            },
          });
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <Helmet>
        <title>Cryptolocally: Create Trade</title>
      </Helmet>
      {!offer ? <Loader className="absolute self-center justify-self-center top-0 h-screen" />
        : (
          <div className="flex flex-col gap-12 py-5 w-full max-w-[1120px] self-center">
            <Card className="py-5 px-6">
              <div className="flex items-center w-full gap-2 text-secondary">
                <ExclamationIcon className="w-6 h-6" />
                <p className="flex-1">The escrow is there to protect both sides. For a safe transaction, never pay before the escrow is funded.</p>
              </div>
            </Card>
            <div className="flex justify-between gap-[12%] max-lg:gap-[7%] max-md:flex-col max-md:items-center">
              <OfferTradeDetails
                offer={offer}
                amount={demandedAmount}
                isBuyer={isBuyer}
                isSeller={isSeller}
              />
              <div className="flex-1 max-w-[500px]">
                <UserInfo offer={offer} className="mb-6" />
                <Card className="p-6 flex-1 max-w-[500px] w-full h-fit">
                  <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col self-center gap-6 w-full">
                    <div className="flex flex-col gap-4">
                      <div>
                        <div className="flex justify-between items-center gap-3 mb-2">
                          <h6>{currentMethod?.value}</h6>
                          {limitsValue && (
                          <div className="flex gap-4 flex-wrap">
                            <div className="flex gap-2 flex-wrap text-secondary">
                              <span>{limitsTitle}</span>
                              <span className="text-blue">{limitsValue}</span>
                            </div>
                          </div>
                          )}
                        </div>

                        <FormInput
                          name="demandedAmount"
                          control={control}
                          // min={offer.minTrade}
                          // max={offer.maxTrade}
                          type="number"
                          step="0.0000001"
                          placeholder="0.00"
                          suffixIcon={offer.demandedCurrency}
                          error={errors.demandedAmount}
                        />
                      </div>
                      <div>
                        <h6 className="mb-2">{isSeller ? 'Payment Method' : 'Payout'}</h6>
                        <FormInput
                          name="offeredAmount"
                          control={control}
                          placeholder="0.00"
                          type="number"
                          step="0.0000001"
                          suffixIcon={offer?.offeredCurrency}
                          error={errors.offeredAmount}
                          blocked
                        />
                      </div>
                    </div>
                    <p className="text-[14px] [&_a]:underline [&_a]:text-tertiary text-secondary">
                      By opening this trade you agree to the
                      {' '}
                      <NavLink to={Paths.TermsOfUse}>Terms of Use</NavLink>
                      {' '}
                      and
                      {' '}
                      <NavLink to={Paths.PrivacyPolicy}>Privacy Policy</NavLink>
                      .
                    </p>
                    <Button
                      category={ButtonCategory.Filled}
                      type="submit"
                      name="publish"
                      isLoading={isLoading}
                    >
                      Open Trade
                    </Button>
                  </form>
                </Card>
              </div>
            </div>
          </div>
        )}
    </>
  );
};
